import * as FleetFile from 'types/fleet-files';
import { Page } from 'types/pageable';
import { EquipmentType } from 'types/damage';
import { authorizedFetch, serverMessage } from './authorized-fetch';

/*
 * Fleet lookup client.
 *
 * Exists as its own service rather than another `fetchWithRetry` caller because that helper
 * collapses every non-OK response to a caller-supplied default. That is the right behaviour for its
 * other consumers and exactly the wrong behaviour here: the whole point of the new backend contract
 * is that a provider outage, malformed provider data, an authorization denial and a genuine missing
 * unit are four different answers. A generic `null` fallback throws that away again.
 *
 * Token handling lives in `authorizedFetch`, shared with the other admin writes that must not
 * collapse failures into a default.
 */

const BASE_URL = '/api/fleet-files';

const readErrorBody = async (response: Response): Promise<FleetFile.FleetErrorBody> => {
  const fallback: FleetFile.FleetErrorBody = {
    status: response.status,
    errorCode: 'FLEET_UPSTREAM_UNAVAILABLE',
    retryable: true
  };
  try {
    const body = await response.json();
    if (body && typeof body === 'object' && 'errorCode' in body) return body as FleetFile.FleetErrorBody;
  } catch {
    /* a failure response without a parseable body is still a failure, just less specific */
  }
  return fallback;
};

/* Maps a server error body to the outcome kind the UI switches on. */
const outcomeForError = (error: FleetFile.FleetErrorBody): FleetFile.FleetLookupOutcome => {
  switch (error.errorCode) {
    case 'FLEET_UPSTREAM_TIMEOUT':
      return { kind: 'timeout', error };
    case 'FLEET_UPSTREAM_UNAVAILABLE':
      return { kind: 'unavailable', error };
    case 'FLEET_INVALID_ENVELOPE':
    case 'FLEET_IDENTITY_MISMATCH':
    case 'FLEET_INVALID_CORE_DATA':
      return { kind: 'invalidData', error };
    case 'FLEET_UPSTREAM_AUTH_FAILED':
      /* the PROVIDER rejected US. Not the user's authorization, and not a missing unit. */
      return { kind: 'unavailable', error };
    case 'FLEET_INVALID_REQUEST':
      return { kind: 'invalidRequest', error };
    default:
      return { kind: 'unavailable', error };
  }
};

/*
 * Resolves one unit. Never returns null and never throws for an expected outcome — every path
 * produces a `FleetLookupOutcome` the caller must switch on.
 */
export const lookupFleetUnit = async (
  locationId: number,
  unitNumber: string,
  equipmentType?: EquipmentType,
  signal?: AbortSignal
): Promise<FleetFile.FleetLookupOutcome> => {
  const url = new URL(`${BASE_URL}/locations/${locationId}/lookup`, window.location.origin);
  url.searchParams.append('unitNumber', unitNumber);
  if (equipmentType) url.searchParams.append('equipmentType', equipmentType);

  try {
    const response = await authorizedFetch(url);

    /* 403 means the caller may not ask. It must never be shown as "unmapped" or "not found". */
    if (response.status === 401 || response.status === 403) {
      return { kind: 'denied', error: await readErrorBody(response) };
    }

    if (!response.ok) {
      return outcomeForError(await readErrorBody(response));
    }

    const record = (await response.json()) as FleetFile.FleetLookupRecord;
    /* defend against an older backend or a proxy returning something unexpected */
    if (!record || typeof record !== 'object' || !('status' in record)) {
      return {
        kind: 'invalidData',
        error: { status: 502, errorCode: 'FLEET_INVALID_ENVELOPE', retryable: false }
      };
    }
    if (record.status === 'DISABLED') return { kind: 'disabled' };
    return record.status === 'FOUND'
      ? { kind: 'found', record: { ...record, issues: record.issues ?? [] } }
      : { kind: 'notFound', record: { ...record, issues: record.issues ?? [] } };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    console.error(e);
    return {
      kind: 'unavailable',
      error: { status: 0, errorCode: 'FLEET_UPSTREAM_UNAVAILABLE', retryable: true }
    };
  }
};

/*
 * A fleet admin read or write the server refused (customer links, size-type crosswalk). Carries the
 * status so a screen can tell a validation problem (400) from a concurrent change (409), and the
 * server's own message to show in place.
 */
export class FleetLinkError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'FleetLinkError';
  }
}

const linkError = async (response: Response, subject: string): Promise<FleetLinkError> => {
  let message = await serverMessage(response);
  if (!message) {
    message =
      response.status === 403
        ? `You are not allowed to change ${subject}.`
        : `${subject[0].toUpperCase()}${subject.slice(1)} could not be saved (${response.status}).`;
  }
  return new FleetLinkError(response.status, message);
};

const linkRequest = async <T>(url: URL, method: string, body?: unknown, subject = 'fleet file links'): Promise<T> => {
  const response = await authorizedFetch(url, method, body);
  if (!response.ok) throw await linkError(response, subject);
  return (await response.json()) as T;
};

const linksUrl = (locationId: number) =>
  new URL(`${BASE_URL}/locations/${locationId}/customer-links`, window.location.origin);

/* Every external code with the accounts linked to it at a location. */
export const getCustomerLinks = (locationId: number): Promise<FleetFile.CustomerLink[]> =>
  linkRequest(linksUrl(locationId), 'GET');

/* Applies the final link state of each listed account at a location, all or nothing. */
export const putCustomerLinks = (
  locationId: number,
  edits: FleetFile.CustomerLinkEdit[]
): Promise<FleetFile.CustomerLink[]> => linkRequest(linksUrl(locationId), 'PUT', edits);

/* Links one account (or unlinks it with null). Sends no location: the server uses the account's. */
export const putCustomerLink = (customerId: number, code: number | null): Promise<FleetFile.CustomerLink[]> =>
  linkRequest(new URL(`${BASE_URL}/customers/${customerId}/link`, window.location.origin), 'PUT', { code });

/* --- size-type crosswalk (admin) --- */

const SIZE_TYPES = 'fleet size type mappings';
const sizeTypesUrl = (path = '') => new URL(`${BASE_URL}/size-types${path}`, window.location.origin);

export interface SizeTypeMappingQuery {
  filterString?: string;
  resolution?: FleetFile.SizeTypeResolution;
  pageNum?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: string;
}

/* One page of the crosswalk, optionally narrowed to one resolution state. */
export const getSizeTypeMappings = (query: SizeTypeMappingQuery): Promise<Page<FleetFile.SizeType>> => {
  const url = sizeTypesUrl();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.append(key, `${value}`);
  });
  return linkRequest(url, 'GET', undefined, SIZE_TYPES);
};

/* Replaces one row's suggestion and review decision; 409 when the row changed since it was loaded. */
export const updateSizeTypeMapping = (
  id: number,
  update: FleetFile.SizeTypeMappingUpdate
): Promise<FleetFile.SizeType> => linkRequest(sizeTypesUrl(`/${id}`), 'PUT', update, SIZE_TYPES);

/* Adds a row for a provider code that has none yet; 409 when the code already has one. */
export const createSizeTypeMapping = (create: FleetFile.SizeTypeMappingCreate): Promise<FleetFile.SizeType> =>
  linkRequest(sizeTypesUrl(), 'POST', create, SIZE_TYPES);

/* Provider size-type codes the gate lookup has met, optionally filtered by last outcome. */
export const getSizeTypeSightings = (
  outcomes: FleetFile.SizeTypeSightingOutcome[] = [],
  sort: 'count' | 'lastSeen' = 'count'
): Promise<FleetFile.SizeTypeSighting[]> => {
  const url = sizeTypesUrl('/sightings');
  outcomes.forEach(outcome => url.searchParams.append('outcome', outcome));
  url.searchParams.append('sort', sort);
  return linkRequest(url, 'GET', undefined, SIZE_TYPES);
};

export { outcomeForError as __outcomeForErrorForTest };
